import type { TFunction } from 'i18next';
import { Link } from 'react-router-dom';
import { formatClock } from '@/features/trips/format';
import { ignitionPin } from './types';
import type { IgnitionAlert } from './types';

/**
 * An ignition event in the alerts feed (slice 54) — the SEVENTH item shape. The
 * engine was started (`ignition_on`) or stopped (`ignition_off`). Both are
 * severity `info`: a routine operational fact a fleet manager opted in to see, not
 * an incident, so the row is QUIET throughout — the same restraint `power_restored`
 * and `tracker_online` show, NEVER attention weight. There is no critical styling
 * here at any point; the pin below does not change that.
 *
 * 🔴 SLICE 56b — IT NOW CARRIES A LAST-KNOWN LOCATION, AND THAT REVERSES THE OLD
 * COMMENT. Mahad, 20 Sep: tapping an `ignition_off` alert should open the map at the
 * vehicle's last known place, the way `power_cut`/`tamper` already do — an engine
 * event happens somewhere, and a fleet manager wants to see where. So the wire now
 * carries the vehicle's LAST VALID fix at/before the transition frame as nullable
 * `observedLat`/`observedLon` (both set, or both null — never invented from live
 * state; ADR 0011 decision 2, position 27). Read ONLY through `ignitionPin`. So,
 * exactly like `PowerRow`/`TamperRow`:
 *   - WITH a pin → a select button (like `OverspeedRow`): selecting it centres the
 *     fleet map on the last-known fix (and marks read). On vehicle detail (no map)
 *     selecting just marks it read.
 *   - WITHOUT a pin → a `Link` straight to the vehicle, the honest destination for a
 *     location-less event, exactly as it was before this slice.
 * The pin is LABELLED "last known location" so it is never read as the exact spot the
 * key turned. Because ignition stays QUIET, there is no "no location" line when
 * unpinned — the plain Link is the whole affordance.
 *
 * These alerts are OFF by default per tenant and only ever reach the feed when an
 * owner/manager turns the ignition switch on in Settings (the industry-standard
 * opt-in, PLAN). Tokens only; no hex literal enters this file.
 */
export function IgnitionRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: IgnitionAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: IgnitionAlert) => void;
  t: TFunction;
}) {
  const on = alert.type === 'ignition_on';
  const titleKey = on ? 'alerts.ignition.on.title' : 'alerts.ignition.off.title';
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const pin = ignitionPin(alert);

  // Quiet throughout: an info event never carries attention weight — with a pin or
  // without one. The unread dot is slate (not the accent an attention row uses), and
  // the title stays medium. A selected row gets the primary tint every positioned
  // row uses, but that is selection, not severity.
  const titleClass = 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : 'bg-raad-slate';

  const containerClass = [
    'block w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
    selected
      ? 'border-raad-primary/40 bg-raad-primary/10'
      : 'border-raad-line-5 bg-raad-tile/60 hover:border-raad-line-12',
  ].join(' ');

  // Shared inner content for both wrappers (button when positioned, Link otherwise).
  const inner = (
    <div className="flex items-start gap-2.5">
      <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={['truncate text-sm', titleClass].join(' ')}>{t(titleKey)}</span>
          <span
            data-testid="ignition-tag"
            className="flex-none rounded-md bg-raad-slate/12 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-slate"
          >
            {t('alerts.ignition.tag')}
          </span>
          {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
        </div>

        {plate ? (
          <div className="mt-0.5 truncate text-[11.5px] font-semibold text-raad-dim">{plate}</div>
        ) : null}

        <div className="mt-1 text-[12px] text-raad-muted">
          {t('alerts.observedAt', { time: observedTime })}
        </div>

        {/* The last-known location line, present ONLY when there is a pin. Quiet
            (muted, not the power row's accent) so an ignition event never draws the
            eye like a power cut; the label states, honestly, that this is the last
            known location, never the exact spot the ignition turned. */}
        {pin ? (
          <div
            data-testid="ignition-last-known"
            className="mt-1 flex items-center gap-1.5 text-[12px] font-medium text-raad-muted"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11Z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            {t('alerts.ignition.lastKnownLocation')}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <li>
      {pin ? (
        // Positioned: selecting centres the fleet map on the last-known fix (and
        // marks read), exactly like OverspeedRow / PowerRow / TamperRow.
        <button
          type="button"
          onClick={() => onSelect(alert)}
          aria-pressed={selected}
          data-testid="ignition-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="true"
          className={containerClass}
        >
          {inner}
        </button>
      ) : (
        // Location-less: a Link to the vehicle — the honest destination — never a
        // map-select button that would centre on nothing.
        <Link
          to={`/vehicles/${alert.vehicleId}`}
          data-testid="ignition-row"
          data-type={alert.type}
          data-severity={alert.severity}
          data-alert-id={alert.id}
          data-has-pin="false"
          className={containerClass}
        >
          {inner}
        </Link>
      )}
    </li>
  );
}
